import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import authService, { AccessProfile, HomepageCategory } from '../services/auth.service';
import VolumeMonitor from './VolumeMonitor';
import ActivityMonitor from './ActivityMonitor';
import HomepageSettings from './HomepageSettings';
import AccessProfiles from './AccessProfiles';
import UserGroupsPanel from '../components/UserGroupsPanel';
import ModuleAccessGrid from '../components/ModuleAccessGrid';
import '../components/ModuleAccessGrid.css';
// import logoZitata from '../assets/images/logo_zitata.png';

interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  active: boolean;
  lastLogin: string;
  createdAt: string;
  moduleAccess?: { [key: string]: boolean } | string;
  profileId?: number | null;
}

interface Module {
  id: string;
  name: string;
  description: string;
  icon?: string;
}

const DEFAULT_MODULES: Module[] = [
  { id: 'dashboard', name: 'Dashboard', description: 'Tableau de bord principal' },
  { id: 'rh', name: 'Tâches', description: 'Tâches, validation et modèles' },
  { id: 'planning', name: 'Planning', description: 'Planning et congés des salariés' },
  { id: 'programming', name: 'Programmation', description: 'Gestion des grilles de programmes' },
  { id: 'playlist', name: 'Playlist', description: 'Génération, édition et diffusion des playlists' },
  { id: 'filemanager', name: 'File Manager', description: 'Gestion des fichiers Hyperdeck, Shows et Playlists' },
  { id: 'conducteur', name: 'Conducteur', description: 'Gestion des conducteurs d\'émissions en direct' },
  { id: 'magasin', name: 'Magasin', description: 'Gestion du matériel et des packs caméra' },
  { id: 'captations', name: 'Captations', description: 'Dossiers de tournage : checklists matériel, notes, photos et fichiers techniques' },
  { id: 'vault', name: 'Vault', description: 'Génération et stockage de mots de passe et licences' },
  { id: 'live', name: 'Live', description: 'Diffusion HLS & statistiques en temps réel' },
  { id: 'alerting', name: 'Alerting', description: 'Alertes SNMP et notifications' },
  { id: 'videowall', name: 'Mur d\'image', description: 'Contrôle des murs vidéo via vMix - animations, montages photos' },
  { id: 'rustdesk', name: 'RustDesk Monitor', description: 'Historique et monitoring des connexions distantes RustDesk' },
  { id: 'bescor', name: 'Bescor PTZ', description: 'Contrôle des 6 têtes pan-tilt Bescor MP-101' },
  { id: 'clim', name: 'Climatisation', description: 'Marche/arrêt des climatiseurs IR par zone (plateaux TV, accueils)' },
  { id: 'portal', name: 'Portail', description: 'Accès simplifié pour présentateurs (réservations, conducteur)' },
  { id: 'volumes', name: 'Moniteur de Volumes', description: 'Surveillance et remontage des volumes réseau' },
  { id: 'network', name: 'Network', description: 'Inventaire IP, switchs et topologie réseau' },
  { id: 'appconsole', name: 'Console App', description: 'Analytics, jeux concours et notifications de l\'app mobile' },
  { id: 'postiz', name: 'Réseaux Sociaux', description: 'Publication automatique et manuelle sur Instagram, Facebook, TikTok et YouTube (Postiz)' },
  { id: 'timer', name: 'Timer', description: 'Décompte et horloge des plateaux 1 & 2' },
  { id: 'hyperdeck', name: 'HyperDeck', description: 'Enregistrement, status et transferts HyperDeck' },
  { id: 'contacts', name: 'Invités', description: 'Carnet des invités collecté via le Wi-Fi (synthés vMix et relances commerciales)' },
  { id: 'lexique', name: 'Lexique', description: 'Conventions de nommage et champs lexical audiovisuel / TV' },
  { id: 'extraits', name: 'Extraits', description: 'Propositions xAI d\'extraits réseaux sociaux à sélectionner par émission' },
  { id: 'redaction_jt', name: 'NiouZ', description: 'Veille, Rédacteur, Zi Bot et conducteur du JT du soir' },
  { id: 'regie_pub', name: 'Régie Pub', description: 'CRM commercial : prospects, clients, carte Martinique, propositions et campagnes' },
  { id: 'achats', name: 'Achats', description: 'Listes d\'achats de matériel, quantités, prix et priorités' }
];

const mergeModules = (modules: Module[]) => {
  const map = new Map(modules.map((mod) => [mod.id, mod]));
  DEFAULT_MODULES.forEach((mod) => {
    if (!map.has(mod.id)) {
      map.set(mod.id, mod);
    } else if (mod.id === 'rh') {
      map.set(mod.id, { ...map.get(mod.id)!, name: mod.name, description: mod.description });
    }
  });
  return Array.from(map.values());
};

interface UserFormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: string;
  moduleAccess: { [key: string]: boolean };
  changePassword?: boolean;
  profileId?: number | null;
  contractType?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  workSchedule?: string;
  weeklyHours?: number;
}

// up === null : sonde jamais aboutie (état inconnu), à distinguer d'un service
// réellement DOWN pour ne pas afficher une fausse panne au premier rendu.
interface Health {
  up: boolean | null;
  latencyMs: number | null;
}

const HEALTH_UNKNOWN: Health = { up: null, latencyMs: null };
// Doit rester au-dessus du proxy_connect_timeout de nginx (10 s) : un service
// vraiment arreté répond par un refus de connexion immédiat, donc une attente
// longue ne signale jamais qu'une reprise réseau en cours.
const PROBE_TIMEOUT_MS = 15000;

const fetchProbe = async (url: string, timeoutMs = PROBE_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { cache: 'no-store', signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

// Quitter la page annule les requêtes en vol. Cet échec ne dit rien de la santé
// du service : on conserve alors l'état précédent au lieu de le passer à DOWN.
const isPageLeaving = () => document.visibilityState === 'hidden';

const profileBadgeStyle = (color?: string): React.CSSProperties | undefined => {
  if (!color || !/^#[0-9a-fA-F]{6}$/.test(color)) return undefined;
  const hex = color.slice(1);
  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  return {
    background: `rgba(${red}, ${green}, ${blue}, 0.16)`,
    color,
    borderColor: `rgba(${red}, ${green}, ${blue}, 0.45)`,
  };
};

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingRedirect, setCheckingRedirect] = useState(true); // 🆕 État pour éviter le flash
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showVolumeMonitor, setShowVolumeMonitor] = useState(false);
  const [showActivityMonitor, setShowActivityMonitor] = useState(false);
  const [showHomepageSettings, setShowHomepageSettings] = useState(false);
  const [resourcePanel, setResourcePanel] = useState<'rooms' | 'vehicles' | null>(null);
  const [homepageCategories, setHomepageCategories] = useState<HomepageCategory[]>([]);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showAutresSection, setShowAutresSection] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [rightsOpen, setRightsOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'user',
    moduleAccess: {},
    profileId: null
  });
  const [accessProfiles, setAccessProfiles] = useState<AccessProfile[]>([]);
  const [userPane, setUserPane] = useState<'people' | 'groups' | 'profiles'>('people');
  const [userQuery, setUserQuery] = useState('');
  const [userSort, setUserSort] = useState<'name' | 'email' | 'role' | 'status' | 'login'>('name');
  const [extraitsShows, setExtraitsShows] = useState<{ abbreviation: string; title: string }[]>([]);
  const [extraitsShowsError, setExtraitsShowsError] = useState<string | null>(null);
  const [extraitsShowQuery, setExtraitsShowQuery] = useState('');
  const [achatsLists, setAchatsLists] = useState<{ id: string; name: string }[]>([]);
  const [achatsListsError, setAchatsListsError] = useState<string | null>(null);
  const [captationsSites, setCaptationsSites] = useState<{ id: string; name: string }[]>([]);
  const [captationsSitesError, setCaptationsSitesError] = useState<string | null>(null);
  const [vaultGroups, setVaultGroups] = useState<{ id: string; name: string }[]>([]);
  const [vaultGroupsError, setVaultGroupsError] = useState<string | null>(null);

  const reloadProfiles = useCallback(async () => {
    try {
      const response = await authService.getAccessProfiles();
      if (response.success && response.profiles) setAccessProfiles(response.profiles);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (showUserManagement && user?.role === 'admin') reloadProfiles();
  }, [showUserManagement, user?.role, reloadProfiles]);

  const sortedUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    const profileName = (id?: number | null) => accessProfiles.find((profile) => profile.id === id)?.name || '';
    const list = users.filter((item) => {
      if (!q) return true;
      const name = `${item.firstName || ''} ${item.lastName || ''} ${item.email} ${profileName(item.profileId)}`.toLowerCase();
      return name.includes(q);
    });
    const fold = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const byName = (item: User) => fold(`${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email);
    list.sort((a, b) => {
      if (userSort === 'email') return fold(a.email).localeCompare(fold(b.email));
      if (userSort === 'role') return fold(a.role).localeCompare(fold(b.role)) || byName(a).localeCompare(byName(b));
      if (userSort === 'status') return Number(b.active) - Number(a.active) || byName(a).localeCompare(byName(b));
      if (userSort === 'login') {
        const ta = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
        const tb = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
        return tb - ta || byName(a).localeCompare(byName(b));
      }
      return byName(a).localeCompare(byName(b)) || fold(a.email).localeCompare(fold(b.email));
    });
    return list;
  }, [users, userQuery, userSort, accessProfiles]);
  const [programmingHealth, setProgrammingHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [hrHealth, setHrHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [planningHealth, setPlanningHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [playlistHealth, setPlaylistHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [agentHealth, setAgentHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [davinciStatus, setDavinciStatus] = useState<{ running: boolean | null; projectOpen: boolean; projectName: string | null }>({
    running: null,
    projectOpen: false,
    projectName: null,
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [agentWatcher, setAgentWatcher] = useState<{ isRunning: boolean; watchedPaths: number; filesTracked: number }>({ isRunning: false, watchedPaths: 0, filesTracked: 0 });
  const [hlsHealth, setHlsHealth] = useState<Health & { online: boolean; viewers: number }>({ ...HEALTH_UNKNOWN, online: false, viewers: 0 });
  const [alertingHealth, setAlertingHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [conducteurHealth, setConducteurHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [magasinHealth, setMagasinHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [vaultHealth, setVaultHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [videowallHealth, setVideowallHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [rustdeskHealth, setRustdeskHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [bescorHealth, setBescorHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [climHealth, setClimHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [timerHealth, setTimerHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [hyperdeckHealth, setHyperdeckHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [volumesHealth, setVolumesHealth] = useState<{ loaded: boolean; total: number; mounted: number }>({ loaded: false, total: 0, mounted: 0 });
  const [networkHealth, setNetworkHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [contactsHealth, setContactsHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [lexiqueHealth, setLexiqueHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [extraitsHealth, setExtraitsHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [redactionJtHealth, setRedactionJtHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [regiePubHealth, setRegiePubHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [achatsHealth, setAchatsHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [captationsHealth, setCaptationsHealth] = useState<Health>(HEALTH_UNKNOWN);
  const [postizHealth, setPostizHealth] = useState<Health>(HEALTH_UNKNOWN);

  // Déclarer fetchData avant l'effet pour éviter l’erreur d’ordre

  const fetchData = useCallback(async () => {
    try {
      const [usersResponse, modulesResponse, homepageResponse] = await Promise.all([
        authService.getUsers(),
        authService.getModules(),
        authService.getHomepageLayout().catch(() => ({ success: false as const, categories: [] }))
      ]);

      if (usersResponse.success && usersResponse.users) {
        setUsers(usersResponse.users);
      }

      if (modulesResponse.success && modulesResponse.modules) {
        setModules(mergeModules(modulesResponse.modules));
      }

      if (homepageResponse.success && homepageResponse.categories) {
        setHomepageCategories(homepageResponse.categories);
      }
    } catch (err: any) {
      setError('Erreur lors du chargement des données');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    if (!user || !authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchData();
    // Force la navigation dans le même onglet pour tout lien vers /programming/ ou /hr/
    const clickHandler = (e: any) => {
      const anchor = (e.target as HTMLElement)?.closest && (e.target as HTMLElement).closest('a[href]');
      const href = anchor && anchor.getAttribute('href');
      if (href && (href.includes('/programming') || href.includes('127.0.0.1/programming'))) {
        e.preventDefault();
        const t = authService.getToken?.() || localStorage.getItem('token') || '';
        const rt = localStorage.getItem('refreshToken') || '';
        const qs: string[] = [];
        if (t) qs.push(`token=${encodeURIComponent(t)}`);
        if (rt) qs.push(`rt=${encodeURIComponent(rt)}`);
        const q = qs.length > 0 ? `?${qs.join('&')}` : '';
        window.location.assign(`${window.location.origin}/programming/${q}`);
        return;
      }
      if (href && (href.includes('/hr') || href.includes('127.0.0.1/hr'))) {
        e.preventDefault();
        window.location.assign(`${window.location.origin}/hr/`);
      }
      if (href && (href.includes('/alerting') || href.includes('127.0.0.1/alerting'))) {
        e.preventDefault();
        window.location.assign(`${window.location.origin}/alerting/`);
      }
      if (href && (href.includes('/vault') || href.includes('127.0.0.1/vault'))) {
        e.preventDefault();
        window.location.assign(`${window.location.origin}/vault/`);
      }
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [user, navigate, fetchData]);

  // 🚀 Redirection automatique si l'utilisateur n'a accès qu'à un seul module
  useEffect(() => {
    // Attendre que les modules soient chargés et que le loading soit terminé
    if (loading || modules.length === 0 || !user) return;
    
    // Ne pas rediriger les admins (ils ont toujours accès au dashboard complet)
    if (user.role === 'admin') {
      setCheckingRedirect(false);
      return;
    }
    
    // Calculer les modules accessibles
    const getUserAccess = () => {
      try {
        const ma = user.moduleAccess;
        if (!ma) return {};
        if (typeof ma === 'string') {
          try { return JSON.parse(ma); } catch { return {}; }
        }
        return ma;
      } catch { return {}; }
    };
    
    const userAccess = getUserAccess();
    if (userAccess.rh && !Object.prototype.hasOwnProperty.call(userAccess, 'planning')) {
      userAccess.planning = { view: true };
    }
    if (userAccess.programming && !Object.prototype.hasOwnProperty.call(userAccess, 'playlist')) {
      userAccess.playlist = { view: true };
    }
    const excludedModules = ['dashboard'];
    const accessibleMods = modules
      .filter(m => !excludedModules.includes(m.id.toLowerCase()))
      .filter(m => userAccess[m.id]);
    
    // Si un seul module accessible, rediriger directement
    if (accessibleMods.length === 1) {
      const singleModule = accessibleMods[0];
      const moduleId = singleModule.id.toLowerCase();
      
      console.log(`🚀 Redirection automatique vers le module unique: ${singleModule.name}`);
      
      if (moduleId.includes('program')) {
        window.location.assign(`${window.location.origin}/programming/`);
      } else if (moduleId === 'rh') {
        window.location.assign(`${window.location.origin}/hr/`);
      } else if (moduleId === 'planning') {
        window.location.assign(`${window.location.origin}/planning/`);
      } else if (moduleId === 'playlist') {
        window.location.assign(`${window.location.origin}/playlist/`);
      } else if (moduleId === 'filemanager') {
        window.location.assign(`${window.location.origin}/file-manager/`);
      } else if (moduleId === 'alerting') {
        window.location.assign(`${window.location.origin}/alerting/`);
      } else if (moduleId === 'vault') {
        window.location.assign(`${window.location.origin}/vault/`);
      } else if (moduleId === 'regie_pub') {
        window.location.assign(`${window.location.origin}/regie-pub/`);
      } else if (moduleId === 'extraits') {
        window.location.assign(`${window.location.origin}/extraits/`);
      } else if (moduleId === 'achats') {
        window.location.assign(`${window.location.origin}/achats/`);
      } else if (moduleId === 'captations') {
        window.location.assign(`${window.location.origin}/captations/`);
      } else if (moduleId === 'presonus') {
        window.location.assign(`${window.location.origin}/presonus/`);
      } else {
        navigate(`/${singleModule.id}`);
      }
      // Ne pas setCheckingRedirect(false) ici car on redirige
    } else {
      // Plusieurs modules ou aucun → afficher le dashboard
      setCheckingRedirect(false);
    }
  }, [loading, modules, user, navigate]);

  // Sonde générique UP/DOWN + latence. Renvoie l'état obtenu pour permettre à
  // l'appelant d'enchaîner (cf. checkAgentHealth).
  //
  // Un seul échec ne fait pas basculer la tuile en DOWN : l'agent natif est
  // joint via le NAT de Docker Desktop, qui perd des paquets par salves. Un
  // 504 isolé est donc du bruit réseau, pas une panne, et la sonde suivante
  // repasse. Il faut deux échecs consécutifs pour annoncer DOWN.
  const failStreaks = useRef<{ [url: string]: number }>({});
  const probe = async (url: string, setState: React.Dispatch<React.SetStateAction<Health>>): Promise<boolean> => {
    const t0 = performance.now();
    const fail = () => {
      if (isPageLeaving()) return;
      const streak = (failStreaks.current[url] || 0) + 1;
      failStreaks.current[url] = streak;
      if (streak >= 2) setState({ up: false, latencyMs: null });
      else setState((prev) => (prev.up === true ? { up: true, latencyMs: prev.latencyMs } : HEALTH_UNKNOWN));
    };
    try {
      const res = await fetchProbe(url);
      if (!res.ok) {
        fail();
        return false;
      }
      failStreaks.current[url] = 0;
      setState({ up: true, latencyMs: Math.round(performance.now() - t0) });
      return true;
    } catch (e) {
      fail();
      return false;
    }
  };

  // Programming : endpoint public garanti (évite les 401 bruyants)
  const checkProgrammingHealth = () => probe('/api/programming/public/items/ad-slots/debug-count', setProgrammingHealth);
  const checkHrHealth = () => probe('/api/hr/health', setHrHealth);
  const checkPlanningHealth = () => probe('/planning/', setPlanningHealth);
  const checkPlaylistHealth = () => probe('/playlist/', setPlaylistHealth);
  const checkAlertingHealth = () => probe('/api/alerting/health', setAlertingHealth);
  const checkConducteurHealth = () => probe('/api/conducteur/health', setConducteurHealth);
  const checkMagasinHealth = () => probe('/api/magasin/stats', setMagasinHealth);
  const checkVaultHealth = () => probe('/api/vault/health', setVaultHealth);
  const checkVideowallHealth = () => probe('/api/videowall/health', setVideowallHealth);
  const checkBescorHealth = () => probe('/api/bescor/health', setBescorHealth);
  const checkClimHealth = () => probe('/api/clim/health', setClimHealth);
  const checkContactsHealth = () => probe('/api/contacts/health', setContactsHealth);
  // Postiz : nginx cible le backend NestJS, seul composant qui tombe en panne
  // silencieusement (le frontend Next.js continue de répondre, en 500).
  const checkPostizHealth = () => probe('/api/postiz/health', setPostizHealth);
  const checkLexiqueHealth = () => probe('/api/lexique/health', setLexiqueHealth);
  const checkExtraitsHealth = () => probe('/api/extraits/health', setExtraitsHealth);
  const checkRedactionJtHealth = () => probe('/api/redaction-jt/health', setRedactionJtHealth);
  const checkRegiePubHealth = () => probe('/api/regie-pub/health', setRegiePubHealth);
  const checkAchatsHealth = () => probe('/api/achats/health', setAchatsHealth);
  const checkCaptationsHealth = () => probe('/api/captations/health', setCaptationsHealth);
  const checkTimerHealth = () => probe('/timer/health', setTimerHealth);
  const checkHyperdeckHealth = () => probe('/api/hyperdeck/status', setHyperdeckHealth);
  const checkNetworkHealth = () => probe('/api/network/health', setNetworkHealth);
  const checkRustdeskHealth = () => probe('/api/rustdesk/health', setRustdeskHealth);

  const checkDavinciStatus = async () => {
    try {
      const res = await fetchProbe('/api/agent/davinci/status', 12000);
      if (!res.ok) throw new Error('status');
      const body = await res.json();
      const data = body.data || {};
      failStreaks.current['davinci'] = 0;
      setDavinciStatus({
        running: !!data.running,
        projectOpen: !!data.projectOpen,
        projectName: data.projectName || null,
      });
    } catch {
      if (isPageLeaving()) return;
      const streak = (failStreaks.current['davinci'] || 0) + 1;
      failStreaks.current['davinci'] = streak;
      if (streak >= 2) {
        setDavinciStatus({ running: false, projectOpen: false, projectName: null });
      }
    }
  };

  // Santé Agent Service (hors Docker) + détails du watcher MEDIAS2
  const checkAgentHealth = async () => {
    const up = await probe('/api/agent/health', setAgentHealth);
    if (!up) {
      setAgentWatcher({ isRunning: false, watchedPaths: 0, filesTracked: 0 });
      return;
    }
    try {
      const watcherRes = await fetchProbe('/api/agent/media/watcher/status');
      if (!watcherRes.ok) return;
      const data = await watcherRes.json();
      setAgentWatcher({
        isRunning: data.data?.isRunning || false,
        watchedPaths: data.data?.watchedPaths?.length || 0,
        filesTracked: data.data?.stats?.filesTracked || 0
      });
    } catch (e) {
      // Détail secondaire : l'état de santé de l'agent reste celui de la sonde.
    }
  };

  // Santé HLS Live Service Natif (UP/DOWN + latence + statut flux)
  const checkHlsHealth = async () => {
    const t0 = performance.now();
    try {
      // Le live est hébergé sur l'Ubuntu dédié : on interroge directement son API de statut.
      // (CORS doit être autorisé côté Ubuntu pour zitata.info — sinon le fetch échoue → DOWN, sans erreur bloquante.)
      const res = await fetchProbe('https://live.zitata.tv/api/hls-native/dashboard');
      if (!res.ok) {
        setHlsHealth({ up: false, latencyMs: null, online: false, viewers: 0 });
        return;
      }
      const data = await res.json();
      const viewers = data.stats?.currentViewers ?? data.viewers ?? 0;
      const online = data.stream?.online ?? data.online ?? viewers > 0;
      setHlsHealth({
        up: true,
        latencyMs: Math.round(performance.now() - t0),
        online: !!online,
        viewers
      });
    } catch (e) {
      if (!isPageLeaving()) setHlsHealth({ up: false, latencyMs: null, online: false, viewers: 0 });
    }
  };

  // Statut volumes réseau (vert = tous montés, orange = partiels, rouge = aucun)
  const checkVolumesHealth = async () => {
    const key = 'volumes';
    const fail = () => {
      if (isPageLeaving()) return;
      const streak = (failStreaks.current[key] || 0) + 1;
      failStreaks.current[key] = streak;
      // Même tolérance que probe() : un échec isolé n'annonce pas DÉCONNECTÉ.
      if (streak >= 2) setVolumesHealth({ loaded: true, total: 0, mounted: 0 });
    };
    try {
      const res = await fetchProbe('/api/agent/volumes/status');
      if (!res.ok) {
        fail();
        return;
      }
      const data = await res.json();
      const vols = data.volumes || [];
      failStreaks.current[key] = 0;
      setVolumesHealth({ loaded: true, total: vols.length, mounted: vols.filter((v: any) => v.mounted).length });
    } catch (e) {
      fail();
    }
  };

  useEffect(() => {
    let lastRunAt = 0;

    const checkAll = () => {
      // visibilitychange et pageshow peuvent se suivre de près : on évite la
      // rafale de doublons.
      const now = Date.now();
      if (now - lastRunAt < 2000) return;
      lastRunAt = now;

      checkProgrammingHealth();
      checkHrHealth();
      checkPlanningHealth();
      checkPlaylistHealth();
      checkAgentHealth();
      checkDavinciStatus();
      checkHlsHealth();
      checkAlertingHealth();
      checkConducteurHealth();
      checkMagasinHealth();
      checkVaultHealth();
      checkVideowallHealth();
      checkRustdeskHealth();
      checkBescorHealth();
      checkClimHealth();
      checkTimerHealth();
      checkHyperdeckHealth();
      checkVolumesHealth();
      checkNetworkHealth();
      checkContactsHealth();
      checkLexiqueHealth();
      checkExtraitsHealth();
      checkRedactionJtHealth();
      checkRegiePubHealth();
      checkAchatsHealth();
      checkCaptationsHealth();
      checkPostizHealth();
    };

    checkAll();

    const intervals = [
      setInterval(checkHlsHealth, 10000),
      setInterval(checkProgrammingHealth, 30000),
      setInterval(checkHrHealth, 30000),
      setInterval(checkPlanningHealth, 30000),
      setInterval(checkPlaylistHealth, 30000),
      setInterval(checkAgentHealth, 30000),
      setInterval(checkDavinciStatus, 30000),
      setInterval(checkAlertingHealth, 30000),
      setInterval(checkConducteurHealth, 30000),
      setInterval(checkMagasinHealth, 30000),
      setInterval(checkVaultHealth, 30000),
      setInterval(checkVideowallHealth, 30000),
      setInterval(checkRustdeskHealth, 30000),
      setInterval(checkBescorHealth, 30000),
      setInterval(checkVolumesHealth, 30000),
      setInterval(checkNetworkHealth, 30000),
      setInterval(checkClimHealth, 30000),
      setInterval(checkTimerHealth, 30000),
      setInterval(checkHyperdeckHealth, 30000),
      setInterval(checkContactsHealth, 30000),
      setInterval(checkLexiqueHealth, 30000),
      setInterval(checkExtraitsHealth, 30000),
      setInterval(checkRedactionJtHealth, 30000),
      setInterval(checkRegiePubHealth, 30000),
      setInterval(checkAchatsHealth, 30000),
      setInterval(checkCaptationsHealth, 30000),
      setInterval(checkPostizHealth, 30000)
    ];

    // Le navigateur throttle fortement les timers d'un onglet en arrière-plan :
    // au retour sur le dashboard l'état affiché peut avoir une minute de retard.
    // On resonde donc dès que la page redevient visible, y compris lors d'une
    // restauration depuis l'historique (pageshow).
    const refresh = () => {
      if (document.visibilityState === 'visible') checkAll();
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('pageshow', refresh);

    return () => {
      intervals.forEach(clearInterval);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('pageshow', refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Statut multi-services retiré temporairement)

  const getUserModuleAccess = (): { [key: string]: any } => {
    if (!user) return {};
    try {
      // Gérer les deux cas : objet direct ou chaîne JSON
      let moduleAccess: any = user.moduleAccess || {};
      
      // Si c'est une chaîne, la parser
      if (typeof moduleAccess === 'string') {
        try {
          moduleAccess = JSON.parse(moduleAccess);
        } catch (parseError) {
          console.error('Error parsing moduleAccess JSON:', parseError);
          return {};
        }
      }
      
      // Si c'est un objet, le retourner directement
      if (typeof moduleAccess === 'object' && moduleAccess !== null) {
        let nextAccess = moduleAccess;
        if (nextAccess.rh && !Object.prototype.hasOwnProperty.call(nextAccess, 'planning')) {
          nextAccess = { ...nextAccess, planning: { view: true } };
        }
        if (nextAccess.programming && !Object.prototype.hasOwnProperty.call(nextAccess, 'playlist')) {
          const programmingAccess = nextAccess.programming || {};
          nextAccess = {
            ...nextAccess,
            playlist: {
              view: true,
              generator: !!programmingAccess.playlists_generator,
              missing_files: !!programmingAccess.playlists_missing_files,
              statistics: !!programmingAccess.playlists_statistics,
              media_manager: !!programmingAccess.playlists_media_manager,
              settings: !!programmingAccess.playlists_settings,
            }
          };
        }
        return nextAccess as { [key: string]: boolean };
      }
      
      return {};
    } catch (e) {
      console.error('Error accessing moduleAccess:', e);
      return {};
    }
  };

  const getAccessibleModules = () => {
    const userAccess = getUserModuleAccess();
    // Exclure dashboard (tuile Agent) et rustdesk (accessible via Settings > Autres)
    const excludedModules = ['dashboard', 'rustdesk', 'portal'];
    
    return modules
      .filter(module => !excludedModules.includes(module.id.toLowerCase()))
      .filter(module => userAccess[module.id] || user?.role === 'admin');
  };

  const getHomepageSections = () => {
    const accessible = getAccessibleModules();
    const byId = new Map(accessible.map((mod) => [mod.id, mod]));
    const assigned = new Set<string>();
    const sections = homepageCategories
      .map((category) => {
        const moduleIds = [...category.moduleIds];
        if (moduleIds.includes('rh') && !moduleIds.includes('planning') && byId.has('planning')) {
          moduleIds.splice(moduleIds.indexOf('rh') + 1, 0, 'planning');
        }
        if (moduleIds.includes('programming') && !moduleIds.includes('playlist') && byId.has('playlist')) {
          moduleIds.splice(moduleIds.indexOf('programming') + 1, 0, 'playlist');
        }
        const categoryModules = moduleIds
          .map((id) => byId.get(id))
          .filter((mod): mod is Module => !!mod)
          // L'admin choisit la catégorie ; à l'intérieur, l'ordre est
          // alphabétique et non l'ordre d'assignation.
          .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
        categoryModules.forEach((mod) => assigned.add(mod.id));
        return { id: category.id, name: category.name, modules: categoryModules };
      })
      .filter((section) => section.modules.length > 0);

    const unassigned = accessible
      .filter((mod) => !assigned.has(mod.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));

    if (unassigned.length > 0) {
      sections.push({
        id: '_autres',
        name: sections.length > 0 ? 'Autres' : '',
        modules: unassigned,
      });
    }

    return sections;
  };

  const handleModuleClick = (moduleId: string) => {
    const id = (moduleId || '').toLowerCase();
    // Navigation vers le module
    if (id === 'playlist') {
      window.location.assign(`${window.location.origin}/playlist/`);
      return;
    }
    if (id.includes('program')) {
      window.location.assign(`${window.location.origin}/programming/`);
      return;
    }
    if (id.includes('alerting')) {
      window.location.assign(`${window.location.origin}/alerting/`);
      return;
    }
    if (id.includes('conducteur')) {
      window.location.assign(`${window.location.origin}/conducteur/`);
      return;
    }
    if (id.includes('videowall')) {
      window.location.assign(`${window.location.origin}/videowall/`);
      return;
    }
    if (id === 'volumes') {
      setShowVolumeMonitor(true);
      setShowUserManagement(false);
      setShowActivityMonitor(false);
      setShowHomepageSettings(false);
      setResourcePanel(null);
      return;
    }
    if (id === 'network') {
      window.location.assign(`${window.location.origin}/network/`);
      return;
    }
    if (id === 'appconsole') {
      window.location.assign(`${window.location.origin}/app-console/`);
      return;
    }
    if (id === 'timer') {
      window.location.assign(`${window.location.origin}/timer/`);
      return;
    }
    if (id === 'hyperdeck') {
      window.location.assign(`${window.location.origin}/hyperdeck/`);
      return;
    }
    if (id === 'lexique') {
      window.location.assign(`${window.location.origin}/lexique/`);
      return;
    }
    if (id === 'extraits') {
      window.location.assign(`${window.location.origin}/extraits/`);
      return;
    }
    if (id === 'redaction_jt') {
      window.location.assign(`${window.location.origin}/redaction-jt/`);
      return;
    }
    if (id === 'regie_pub') {
      window.location.assign(`${window.location.origin}/regie-pub/`);
      return;
    }
    if (id === 'achats') {
      window.location.assign(`${window.location.origin}/achats/`);
      return;
    }
    if (id === 'captations') {
      window.location.assign(`${window.location.origin}/captations/`);
      return;
    }
    if (id === 'presonus') {
      window.location.assign(`${window.location.origin}/presonus/`);
      return;
    }
    navigate(`/${moduleId}`);
  };

  // Fermer le dropdown Settings au clic extérieur
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettingsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!showUserForm) return;
    let cancelled = false;
    const token = authService.getToken();
    fetch('/api/programming/items/shows', {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const raw = Array.isArray(data?.data) ? data.data : [];
        const seen = new Set<string>();
        const list: { abbreviation: string; title: string }[] = [];
        for (const item of raw) {
          const abbreviation = String(item?.abbreviation || '').trim().toUpperCase();
          const title = String(item?.title || '').trim();
          if (!abbreviation || !title || seen.has(abbreviation)) continue;
          seen.add(abbreviation);
          list.push({ abbreviation, title });
        }
        list.sort((a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' }));
        setExtraitsShows(list);
        setExtraitsShowsError(list.length === 0 ? 'Aucune émission trouvée dans la programmation.' : null);
      })
      .catch(() => {
        if (!cancelled) setExtraitsShowsError('Impossible de charger la liste des émissions.');
      });
    return () => {
      cancelled = true;
    };
  }, [showUserForm]);

  useEffect(() => {
    if (!showUserForm) return;
    let cancelled = false;
    const loadCatalog = async (url: string) => {
      const token = authService.getToken();
      const response = await fetch(url, {
        cache: 'no-store',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || data?.message || 'chargement impossible');
      const raw = Array.isArray(data?.data) ? data.data : [];
      return raw
        .map((item: { id?: string; name?: string }) => ({
          id: String(item?.id || '').trim(),
          name: String(item?.name || '').trim()
        }))
        .filter((item: { id: string; name: string }) => item.id && item.name)
        .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
    };
    const refresh = () => {
      loadCatalog('/api/achats/lists')
        .then((list) => {
          if (cancelled) return;
          setAchatsLists(list);
          setAchatsListsError(list.length === 0 ? 'Aucune liste d’achats pour le moment.' : null);
        })
        .catch(() => {
          if (!cancelled) setAchatsListsError('Impossible de charger les listes d’achats.');
        });
      loadCatalog('/api/captations/sites')
        .then((list) => {
          if (cancelled) return;
          setCaptationsSites(list);
          setCaptationsSitesError(list.length === 0 ? 'Aucun dossier de captation pour le moment.' : null);
        })
        .catch(() => {
          if (!cancelled) setCaptationsSitesError('Impossible de charger les dossiers de captation.');
        });
      loadCatalog('/api/vault/groups')
        .then((list) => {
          if (cancelled) return;
          setVaultGroups(list);
          setVaultGroupsError(list.length === 0 ? 'Aucun groupe Vault pour le moment.' : null);
        })
        .catch(() => {
          if (!cancelled) setVaultGroupsError('Impossible de charger les groupes Vault.');
        });
    };
    refresh();
    const timer = window.setInterval(refresh, 5000);
    window.addEventListener('focus', refresh);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [showUserForm]);

  const handleUserManagement = () => {
    setShowUserManagement(true);
    setShowVolumeMonitor(false);
    setShowActivityMonitor(false);
    setShowHomepageSettings(false);
    setResourcePanel(null);
    setShowSettingsDropdown(false);
  };

  const handleResourcePanel = (panel: 'rooms' | 'vehicles') => {
    setResourcePanel(panel);
    setShowUserManagement(false);
    setShowVolumeMonitor(false);
    setShowActivityMonitor(false);
    setShowHomepageSettings(false);
    setShowSettingsDropdown(false);
  };

  const handleActivityMonitor = () => {
    setShowActivityMonitor(true);
    setShowUserManagement(false);
    setShowVolumeMonitor(false);
    setShowHomepageSettings(false);
    setResourcePanel(null);
    setShowSettingsDropdown(false);
  };

  const handleHomepageSettings = () => {
    setShowHomepageSettings(true);
    setShowUserManagement(false);
    setShowVolumeMonitor(false);
    setShowActivityMonitor(false);
    setResourcePanel(null);
    setShowSettingsDropdown(false);
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'user',
      moduleAccess: {},
      changePassword: false,
      profileId: null,
      contractType: 'CDI',
      contractStartDate: '',
      contractEndDate: '',
      workSchedule: 'TEMPS_PLEIN',
      weeklyHours: 35
    });
    setRightsOpen(false);
    setFormError(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    // Gérer les deux cas : objet direct ou chaîne JSON
    let moduleAccess: any = user.moduleAccess || {};
    
    // Si c'est une chaîne, la parser
    if (typeof moduleAccess === 'string') {
      try {
        moduleAccess = JSON.parse(moduleAccess);
      } catch (parseError) {
        console.error('Error parsing moduleAccess JSON for editing:', parseError);
        moduleAccess = {};
      }
    }
    
    // Si c'est un objet, l'utiliser directement
    if (typeof moduleAccess === 'object' && moduleAccess !== null) {
      moduleAccess = moduleAccess as { [key: string]: boolean };
    } else {
      moduleAccess = {};
    }

    setFormData({
      email: user.email,
      password: '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role,
      moduleAccess,
      changePassword: false,
      profileId: user.profileId ?? null,
      contractType: 'CDI',
      contractStartDate: '',
      contractEndDate: '',
      workSchedule: 'TEMPS_PLEIN',
      weeklyHours: 35
    });
    setRightsOpen(false);
    setFormError(null);
    setShowUserForm(true);
    if (user.role === 'user') {
      const token = authService.getToken();
      const editedEmail = user.email;
      fetch(`/api/hr/contracts/${user.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((contract) => {
          if (!contract?.contractType) return;
          setFormData((prev) => {
            if (prev.email !== editedEmail || prev.role !== 'user') return prev;
            return {
              ...prev,
              contractType: contract.contractType,
              contractStartDate: String(contract.contractStartDate || '').split('T')[0],
              contractEndDate: contract.contractEndDate ? String(contract.contractEndDate).split('T')[0] : '',
              workSchedule: contract.workSchedule || 'TEMPS_PLEIN',
              weeklyHours: contract.weeklyHours ?? 35
            };
          });
        })
        .catch(() => {});
    }
  };

  const applyAccessProfile = (profileId: string) => {
    if (!profileId) {
      setFormData((prev) => ({ ...prev, profileId: null }));
      return;
    }
    const profile = accessProfiles.find((item) => item.id === Number(profileId));
    if (!profile) return;
    setFormData((prev) => {
      const moduleAccess = JSON.parse(JSON.stringify(profile.moduleAccess || {}));
      const previousShows = (prev.moduleAccess as any)?.extraits?.shows;
      if (
        moduleAccess.extraits
        && typeof moduleAccess.extraits === 'object'
        && Array.isArray(previousShows)
        && previousShows.length > 0
      ) {
        moduleAccess.extraits.shows = previousShows;
        moduleAccess.extraits.full = false;
        moduleAccess.extraits.view = moduleAccess.extraits.view !== false;
        moduleAccess.extraits.select = moduleAccess.extraits.select !== false;
        moduleAccess.extraits.propose = moduleAccess.extraits.propose !== false;
      }
      return {
        ...prev,
        profileId: profile.id,
        moduleAccess
      };
    });
  };

  const handleDeleteUser = async (userId: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      try {
        const response = await authService.deleteUser(userId);
        if (response.success) {
          setUsers(users.filter(u => u.id !== userId));
        } else {
          setError(response.message || 'Erreur lors de la suppression');
        }
      } catch (err: any) {
        setError('Erreur lors de la suppression de l\'utilisateur');
        console.error('Erreur:', err);
      }
    }
  };

  const handleToggleUser = async (userId: number, active: boolean) => {
    try {
      const response = await authService.toggleUser(userId, active);
      if (response.success) {
        setUsers(users.map(u => 
          u.id === userId ? { ...u, active } : u
        ));
      } else {
        setError(response.message || 'Erreur lors de la mise à jour');
      }
    } catch (err: any) {
      setError('Erreur lors de la mise à jour de l\'utilisateur');
      console.error('Erreur:', err);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Pour les admins, ne pas envoyer de moduleAccess restrictif depuis le formulaire :
      // l'auth-service applique ADMIN_MODULE_ACCESS automatiquement côté serveur.
      const {
        contractType,
        contractStartDate,
        contractEndDate,
        workSchedule,
        weeklyHours,
        ...accountFields
      } = formData;
      const access = accountFields.moduleAccess as any;
      const extraits = access?.extraits;
      const limitedShows = extraits && typeof extraits === 'object' && Array.isArray(extraits.shows) && extraits.shows.length > 0;
      const normalized = limitedShows
        ? {
            ...accountFields,
            moduleAccess: {
              ...access,
              extraits: {
                ...extraits,
                full: false,
                view: extraits.view !== false,
                select: extraits.select !== false,
                propose: extraits.propose !== false,
              },
            },
          }
        : accountFields;
      if (!formData.email.trim()) {
        setFormError('L’adresse email est requise');
        return;
      }
      if ((!editingUser || formData.changePassword) && !formData.password.trim()) {
        setFormError('Le mot de passe est requis');
        return;
      }
      const dataToSend = formData.role === 'admin'
        ? { ...accountFields, profileId: null, moduleAccess: {} }
        : normalized;
      setFormError(null);

      let response;
      if (editingUser) {
        response = await authService.updateUser(editingUser.id, dataToSend);
      } else {
        response = await authService.createUser(dataToSend);
      }

      if (response.success) {
        const savedUserId = editingUser?.id ?? response.user?.id;
        if (formData.role === 'user' && contractStartDate && savedUserId) {
          const token = authService.getToken();
          const contractRes = await fetch(`/api/hr/contracts/${savedUserId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              contractType: contractType || 'CDI',
              contractStartDate,
              contractEndDate: contractEndDate || null,
              workSchedule: workSchedule || 'TEMPS_PLEIN',
              weeklyHours: weeklyHours ?? 35
            })
          });
          if (!contractRes.ok) {
            const body = await contractRes.json().catch(() => ({}));
            setFormError(body.error || 'Le compte est enregistré, mais pas le contrat.');
            fetchData();
            return;
          }
        }
        setShowUserForm(false);
        fetchData();
      } else {
        setFormError(response.message || 'Erreur lors de la sauvegarde');
      }
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'Erreur lors de la sauvegarde de l\'utilisateur');
      console.error('Erreur:', err);
    }
  };

  const applyDefaultPermissions = () => {
    const defaultPerms = DEFAULT_ROLE_PERMISSIONS[formData.role];
    if (defaultPerms) {
      setFormData(prev => ({
        ...prev,
        profileId: null,
        moduleAccess: { ...defaultPerms }
      }));
    }
  };

  const toggleExtraitsShow = (abbreviation: string, enabled: boolean) => {
    const key = abbreviation.trim().toUpperCase();
    if (!key) return;
    setFormData(prev => {
      const current = (prev.moduleAccess as any).extraits || {};
      const shows: string[] = Array.isArray(current.shows)
        ? current.shows.map((item: unknown) => String(item).trim().toUpperCase()).filter(Boolean)
        : [];
      const nextShows = enabled
        ? Array.from(new Set([...shows, key]))
        : shows.filter((item) => item !== key);
      const limited = nextShows.length > 0;
      return {
        ...prev,
        moduleAccess: {
          ...prev.moduleAccess,
          extraits: {
            ...current,
            shows: nextShows,
            full: limited ? false : true,
            view: limited ? current.view !== false : true,
            select: limited ? current.select !== false : true,
            propose: limited ? current.propose !== false : true,
          }
        }
      };
    });
  };

  const toggleAchatsList = (listId: string, enabled: boolean) => {
    setFormData(prev => {
      const current = (prev.moduleAccess as any).achats || {};
      const lists: string[] = Array.isArray(current.lists)
        ? current.lists.map((item: unknown) => String(item).trim()).filter(Boolean)
        : [];
      const nextLists = enabled
        ? Array.from(new Set([...lists, listId]))
        : lists.filter((item) => item !== listId);
      return {
        ...prev,
        profileId: null,
        moduleAccess: {
          ...prev.moduleAccess,
          achats: { ...current, lists: nextLists }
        }
      };
    });
  };

  const toggleVaultGroup = (groupId: string, enabled: boolean) => {
    setFormData(prev => {
      const current = (prev.moduleAccess as any).vault || {};
      const groups: string[] = Array.isArray(current.groups)
        ? current.groups.map((item: unknown) => String(item).trim()).filter(Boolean)
        : [];
      const nextGroups = enabled
        ? Array.from(new Set([...groups, groupId]))
        : groups.filter((item) => item !== groupId);
      return {
        ...prev,
        profileId: null,
        moduleAccess: {
          ...prev.moduleAccess,
          vault: { ...current, groups: nextGroups }
        }
      };
    });
  };

  const toggleCaptationsSite = (siteId: string, enabled: boolean) => {
    setFormData(prev => {
      const current = (prev.moduleAccess as any).captations || {};
      const sites: string[] = Array.isArray(current.sites)
        ? current.sites.map((item: unknown) => String(item).trim()).filter(Boolean)
        : [];
      const nextSites = enabled
        ? Array.from(new Set([...sites, siteId]))
        : sites.filter((item) => item !== siteId);
      return {
        ...prev,
        profileId: null,
        moduleAccess: {
          ...prev.moduleAccess,
          captations: { ...current, sites: nextSites }
        }
      };
    });
  };

  // Rôles disponibles
  const ROLE_LABELS: { [key: string]: string } = {
    admin: 'Admin',
    user: 'Salarié',
    prestataire: 'Prestataire',
  };

  // Permissions par défaut selon le rôle
  // RH simplifié: manager = true pour accès complet, sinon employé (accès limité)
  const DEFAULT_ROLE_PERMISSIONS: { [role: string]: { [moduleId: string]: any } } = {
    admin: {
      // Admin a tout par défaut (géré via formData.role === 'admin')
    },
    user: {
      rh: {
        view: true
        // Pas de manager = employé (accès limité à ses propres données)
      },
      planning: {
        view: true
      }
    },
    prestataire: {
      rh: {
        view: true
      },
      planning: {
        view: true
      }
    }
  };

  const renderModuleIcon = (moduleId: string) => {
    const common = { strokeWidth: 1.6 } as any;
    const id = (moduleId || '').toLowerCase();
    if (id.includes('program')) {
      return (
        <svg viewBox="0 0 24 24"><path d="M4 5h16v10H4z" {...common}/><path d="M8 19h8" {...common}/></svg>
      );
    }
    if (id === 'planning') {
      return (
        <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="17" rx="2" {...common}/><path d="M8 2v4M16 2v4M3 9h18" {...common}/></svg>
      );
    }
    if (id === 'playlist') {
      return (
        <svg viewBox="0 0 24 24"><path d="M9 18V6l10-2v12" {...common}/><circle cx="7" cy="18" r="2" {...common}/><circle cx="17" cy="16" r="2" {...common}/></svg>
      );
    }
    if (id === 'rh' || id.includes('hr')) {
      return (
        <svg viewBox="0 0 24 24"><path d="M9 11l3 3L22 4" {...common}/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" {...common}/></svg>
      );
    }
    if (id.includes('filemanager') || id.includes('file-manager')) {
      return (
        <svg viewBox="0 0 24 24"><path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-7l-2-2H5a2 2 0 0 0-2 2z" {...common}/></svg>
      );
    }
    if (id === 'live' || id.includes('hls')) {
      return (
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" {...common}/><path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14" {...common}/></svg>
      );
    }
    if (id === 'alerting') {
      return (
        <svg viewBox="0 0 24 24"><path d="M12 3c-3.3 0-6 2.7-6 6v4l-2 3h16l-2-3V9c0-3.3-2.7-6-6-6z" {...common}/><path d="M9 19a3 3 0 0 0 6 0" {...common}/></svg>
      );
    }
    if (id === 'conducteur') {
      return (
        <svg viewBox="0 0 24 24"><path d="M4 6h16M4 10h16M4 14h10M4 18h6" {...common}/><circle cx="18" cy="16" r="3" {...common}/><path d="M18 14v2h2" {...common}/></svg>
      );
    }
    if (id === 'magasin') {
      return (
        <svg viewBox="0 0 24 24"><rect x="3" y="8" width="18" height="12" rx="1" {...common}/><path d="M7 8V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" {...common}/><circle cx="12" cy="14" r="2" {...common}/></svg>
      );
    }
    if (id === 'vault') {
      return (
        <svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" {...common}/><path d="M7 11V7a5 5 0 0 1 10 0v4" {...common}/></svg>
      );
    }
    if (id === 'rustdesk') {
      return (
        <svg viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" {...common}/><path d="M8 21h8M12 17v4" {...common}/><path d="M17 9l-5 4-5-4" {...common}/></svg>
      );
    }
    if (id === 'bescor') {
      return (
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" {...common}/><path d="M3 12h2M19 12h2M12 3v2M12 19v2" {...common}/><path d="M5.6 5.6l1.4 1.4M16.9 16.9l1.4 1.4M5.6 18.4l1.4-1.4M16.9 7.1l1.4-1.4" {...common}/><rect x="7" y="9" width="10" height="6" rx="1" {...common}/><path d="M17 11l3-2v6l-3-2" {...common}/></svg>
      );
    }
    if (id === 'clim') {
      return (
        <svg viewBox="0 0 24 24"><path d="M12 2v20M4.9 4.9l14.2 14.2M19.1 4.9L4.9 19.1" {...common}/><path d="M12 6l-2-2M12 6l2-2M12 18l-2 2M12 18l2 2" {...common}/><path d="M7.5 8.6l-2.7-.7M7.5 8.6l.7-2.7M16.5 15.4l2.7.7M16.5 15.4l-.7 2.7" {...common}/><path d="M16.5 8.6l2.7-.7M16.5 8.6l-.7-2.7M7.5 15.4l-2.7.7M7.5 15.4l.7 2.7" {...common}/></svg>
      );
    }
    if (id === 'timer') {
      return (
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" {...common}/><path d="M12 7v5l3 2" {...common}/><path d="M12 3v2M5.6 5.6l1.4 1.4" {...common}/></svg>
      );
    }
    if (id === 'hyperdeck') {
      return (
        <svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="2" {...common}/><circle cx="8" cy="12" r="2.5" {...common}/><circle cx="16" cy="12" r="2.5" {...common}/><path d="M8 9.5v5M16 9.5v5" {...common}/></svg>
      );
    }
    if (id === 'portal') {
      return (
        <svg viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" {...common}/><polyline points="16,17 21,12 16,7" {...common}/><line x1="21" y1="12" x2="9" y2="12" {...common}/></svg>
      );
    }
    if (id === 'volumes') {
      return (
        <svg viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="9" ry="3" {...common}/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" {...common}/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" {...common}/></svg>
      );
    }
    if (id === 'network') {
      return (
        <svg viewBox="0 0 24 24"><rect x="2" y="2" width="5" height="5" rx="1" {...common}/><rect x="17" y="2" width="5" height="5" rx="1" {...common}/><rect x="9" y="17" width="6" height="5" rx="1" {...common}/><path d="M4.5 7v3.5a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1V7" {...common}/><path d="M12 11.5V17" {...common}/></svg>
      );
    }
    if (id === 'appconsole') {
      return (
        <svg viewBox="0 0 24 24"><rect x="7" y="2" width="10" height="20" rx="2" {...common}/><path d="M11 18h2" {...common}/><path d="M10 6h4M10 9h4" {...common}/></svg>
      );
    }
    if (id === 'postiz') {
      return (
        <svg viewBox="0 0 24 24"><path d="M3 11l18-7-7 18-3-8-8-3z" {...common}/></svg>
      );
    }
    if (id === 'contacts') {
      // Fiche contact : carte + silhouette + lignes nom/e-mail (distinct de RH).
      return (
        <svg viewBox="0 0 24 24">
          <rect x="3" y="4" width="18" height="16" rx="2" {...common}/>
          <circle cx="9" cy="10" r="2.2" {...common}/>
          <path d="M6.2 16c.5-2 2-3.2 2.8-3.2S11.3 14 11.8 16" {...common}/>
          <path d="M14 9h5M14 13h4" {...common}/>
        </svg>
      );
    }
    if (id === 'lexique') {
      return (
        <svg viewBox="0 0 24 24">
          <path d="M4 5a2 2 0 0 1 2-2h5v18H6a2 2 0 0 0-2 2V5z" {...common}/>
          <path d="M20 5a2 2 0 0 0-2-2h-5v18h5a2 2 0 0 1 2 2V5z" {...common}/>
        </svg>
      );
    }
    if (id === 'extraits') {
      return (
        <svg viewBox="0 0 24 24">
          <rect x="3" y="6" width="18" height="12" rx="2" {...common}/>
          <path d="M3 9h18M7 6v3M7 15v3M17 6v3M17 15v3" {...common}/>
        </svg>
      );
    }
    if (id === 'redaction_jt') {
      return (
        <svg viewBox="0 0 24 24">
          <path d="M4 4h13a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H4z" {...common}/>
          <path d="M4 4v17a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" {...common}/>
          <path d="M8 9h8M8 12.5h8M8 16h5" {...common}/>
        </svg>
      );
    }
    if (id === 'regie_pub') {
      return (
        <svg viewBox="0 0 24 24">
          <path d="M3 11l18-7-7 18-3-8-8-3z" {...common}/>
          <circle cx="12" cy="12" r="2.2" {...common}/>
        </svg>
      );
    }
    if (id === 'achats') {
      return (
        <svg viewBox="0 0 24 24">
          <circle cx="9" cy="20" r="1.4" {...common}/>
          <circle cx="18" cy="20" r="1.4" {...common}/>
          <path d="M3 4h2l2.2 11h11.3l2-7H7" {...common}/>
        </svg>
      );
    }
    if (id === 'captations') {
      return (
        <svg viewBox="0 0 24 24">
          <rect x="2" y="7" width="13" height="10" rx="2" {...common}/>
          <path d="M15 10.5l7-3.5v10l-7-3.5z" {...common}/>
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24"><path d="M6 6h12v12H6z" {...common}/></svg>
    );
  };

  // Afficher un loader pendant le chargement OU la vérification de redirection
  if (loading || checkingRedirect) {
    return (
      <div style={{ 
        background: '#121212', 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        color: '#fff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #353a40',
            borderTop: '3px solid #f59e0b',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Chargement...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ 
        background: '#121212', 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        color: '#fff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ marginBottom: '20px', color: '#94a3b8' }}>Vous devez être connecté pour accéder au dashboard</p>
          <button 
            onClick={() => navigate('/login')}
            style={{
              background: '#f59e0b',
              color: '#181b1f',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  const accessibleModules = getAccessibleModules();
  const homepageSections = getHomepageSections();

  const renderStatusBadge = (up: boolean | null, label?: string) => (
    <span className={`tile-status ${up === null ? 'is-checking' : up ? 'is-up' : 'is-down'}`}>
      {label || (up === null ? '···' : up ? 'UP' : 'DOWN')}
    </span>
  );

  const renderModuleTile = (module: Module) => {
    const idLower = (module.id || '').toLowerCase();
    const isProgramming = idLower.includes('program');
    const isHR = idLower === 'rh' || idLower.includes('hr');
    const isPlanning = idLower === 'planning';
    const isPlaylist = idLower === 'playlist';
    const isFileManager = idLower === 'filemanager' || idLower.includes('file-manager');
    const isLive = idLower === 'live';
    const isAlerting = idLower === 'alerting';
    const isMagasin = idLower === 'magasin';
    const isVault = idLower === 'vault';
    const isRustdesk = idLower === 'rustdesk';
    const isBescor = idLower === 'bescor';
    const isClim = idLower === 'clim';
    const isTimer = idLower === 'timer';
    const isHyperdeck = idLower === 'hyperdeck';
    const isPortal = idLower === 'portal';
    const isVolumes = idLower === 'volumes';
    const isNetwork = idLower === 'network';
    const isPostiz = idLower === 'postiz';
    const isContacts = idLower === 'contacts';
    const isLexique = idLower === 'lexique';
    const isExtraits = idLower === 'extraits';
    const isRedactionJt = idLower === 'redaction_jt';
    const isRegiePub = idLower === 'regie_pub';
    const isAchats = idLower === 'achats';
    const isCaptations = idLower === 'captations';
    const isPresonus = idLower === 'presonus';
    const className = 'tile tile-style-a';

    const health = isProgramming ? programmingHealth
      : isHR ? hrHealth
      : isPlanning ? planningHealth
      : isPlaylist ? playlistHealth
      : isFileManager ? agentHealth
      : isLive ? hlsHealth
      : isAlerting ? alertingHealth
      : idLower === 'conducteur' ? conducteurHealth
      : isMagasin ? magasinHealth
      : isVault ? vaultHealth
      : idLower === 'videowall' ? videowallHealth
      : isRustdesk ? rustdeskHealth
      : isBescor ? bescorHealth
      : isClim ? climHealth
      : isTimer ? timerHealth
      : isHyperdeck ? hyperdeckHealth
      : isNetwork ? networkHealth
      : isContacts ? contactsHealth
      : isLexique ? lexiqueHealth
      : isExtraits ? extraitsHealth
      : isRedactionJt ? redactionJtHealth
      : isRegiePub ? regiePubHealth
      : isAchats ? achatsHealth
      : isCaptations ? captationsHealth
      : isPostiz ? postizHealth
      : null;

    let meta: React.ReactNode = null;
    if (isVolumes && !volumesHealth.loaded) {
      meta = <div className="tile-meta">{renderStatusBadge(null)}</div>;
    } else if (isVolumes) {
      const allMounted = volumesHealth.total > 0 && volumesHealth.mounted === volumesHealth.total;
      const someMounted = volumesHealth.mounted > 0 && volumesHealth.mounted < volumesHealth.total;
      const volColor = allMounted ? '#4ade80' : someMounted ? '#fbbf24' : '#f87171';
      const volBg = allMounted ? 'rgba(74, 222, 128, 0.15)' : someMounted ? 'rgba(251, 191, 36, 0.15)' : 'rgba(248, 113, 113, 0.15)';
      const volBorder = allMounted ? 'rgba(74, 222, 128, 0.3)' : someMounted ? 'rgba(251, 191, 36, 0.3)' : 'rgba(248, 113, 113, 0.3)';
      const volLabel = allMounted ? 'TOUT MONTÉ' : someMounted ? `${volumesHealth.mounted}/${volumesHealth.total} MONTÉS` : 'DÉCONNECTÉ';
      meta = (
        <div className="tile-meta">
          <span className="tile-status" style={{ background: volBg, color: volColor, borderColor: volBorder }}>
            {volLabel}
          </span>
        </div>
      );
    } else if (health) {
      meta = (
        <div className="tile-meta">
          {renderStatusBadge(health.up)}
          {isLive && hlsHealth.online && <span className="tile-status is-live">LIVE</span>}
          {isLive && hlsHealth.viewers > 0 && <span className="tile-latency">👥 {hlsHealth.viewers}</span>}
          {health.latencyMs !== null && (
            <span className="tile-latency">{health.latencyMs}ms</span>
          )}
        </div>
      );
    }

    const inner = (
      <>
        <div className="tile-icon">{renderModuleIcon(isLive ? 'live' : module.id)}</div>
        <div className="tile-body">
          <h3 className="tile-title">
            {module.name}
            {isLive && hlsHealth.online && <span className="tile-live-tag">EN DIRECT</span>}
            {isAlerting && <span className="tile-snmp-tag">SNMP</span>}
          </h3>
          {meta}
        </div>
      </>
    );

    const liveStyle = isLive && hlsHealth.online
      ? { borderColor: 'rgba(0, 210, 106, 0.4)', boxShadow: '0 0 20px rgba(0, 210, 106, 0.15)' }
      : undefined;

    const onClick = () => {
      if (isLive) {
        const live = (user?.role || '').toLowerCase() === 'admin' ? null : (getUserModuleAccess() as any).live || {};
        if (!live || live.view || live.stats) navigate('/live-view');
        else if (live.record) navigate('/live-record');
        else navigate(live.admin ? '/live-admin' : '/live-view');
        return;
      }
      if (isProgramming || isHR || isPlanning || isPlaylist) {
        const t = authService.getToken?.() || localStorage.getItem('token') || '';
        const rt = localStorage.getItem('refreshToken') || '';
        const qs: string[] = [];
        if (t) qs.push(`token=${encodeURIComponent(t)}`);
        if (rt) qs.push(`rt=${encodeURIComponent(rt)}`);
        const q = qs.length > 0 ? `?${qs.join('&')}` : '';
        const path = isProgramming ? 'programming' : isHR ? 'hr' : isPlanning ? 'planning' : 'playlist';
        window.location.assign(`${window.location.origin}/${path}/${q}`);
        return;
      }
      if (isFileManager) { window.location.href = '/filemanager/'; return; }
      if (isAlerting) { window.location.href = '/alerting/'; return; }
      if (isMagasin) { window.location.href = '/magasin/'; return; }
      if (isVault) { window.location.href = '/vault/'; return; }
      if (isRustdesk) { window.location.href = '/rustdesk/'; return; }
      if (isBescor) { window.location.href = '/bescor/'; return; }
      if (isClim) { window.location.href = '/clim/'; return; }
      if (isTimer) { window.location.href = '/timer/'; return; }
      if (isHyperdeck) { window.location.href = '/hyperdeck/'; return; }
      if (isContacts) { window.location.href = '/contacts/'; return; }
      if (isLexique) { window.location.href = '/lexique/'; return; }
      if (isExtraits) { window.location.href = '/extraits/'; return; }
      if (isRedactionJt) { window.location.href = '/redaction-jt/'; return; }
      if (isRegiePub) { window.location.href = '/regie-pub/'; return; }
      if (isAchats) { window.location.href = '/achats/'; return; }
      if (isCaptations) { window.location.href = '/captations/'; return; }
      if (isPresonus) { window.location.href = '/presonus/'; return; }
      if (isPortal) { window.location.href = '/portal/'; return; }
      if (isPostiz) { window.open('https://social.zitata.info', '_blank', 'noopener,noreferrer'); return; }
      handleModuleClick(module.id);
    };

    return (
      <div key={module.id} onClick={onClick} className={className} style={liveStyle}>
        {inner}
      </div>
    );
  };

  return (
    <div className="dash-shell" style={{ 
      background: '#121212', 
      minHeight: '100vh', 
      color: '#fff',
      fontFamily: "system-ui, -apple-system, sans-serif"
    }}>
      {/* Header */}
      <nav style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#121212',
        padding: '0 24px',
        height: '56px',
        borderBottom: '1px solid #353a40',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        {/* Logo - Cliquable pour retourner au dashboard */}
        <div 
          onClick={() => { setShowUserManagement(false); setShowVolumeMonitor(false); setShowActivityMonitor(false); setShowHomepageSettings(false); setResourcePanel(null); }}
          style={{
            background: '#f59e0b',
            borderRadius: '8px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{ 
            color: '#fff', 
            fontWeight: 700, 
            fontSize: '1.25rem',
            letterSpacing: '-0.5px'
          }}>
            Zitata
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Info utilisateur */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#fff' }}>
              {user.firstName} {user.lastName}
            </div>
            <div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {user.role}
            </div>
          </div>
          
          {user.role === 'admin' && (
            <div ref={settingsRef} style={{ position: 'relative' }}>
              {/* Bouton Settings */}
              <button
                onClick={() => { setShowSettingsDropdown(v => { if (v) setShowAutresSection(false); return !v; }); }}
                style={{
                  background: showSettingsDropdown ? '#353a40' : '#23262a',
                  border: `1px solid ${showSettingsDropdown ? '#f59e0b' : '#353a40'}`,
                  color: showSettingsDropdown ? '#f59e0b' : '#cbd5e1',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                Settings
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transition: 'transform 0.2s', transform: showSettingsDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>

              {/* Dropdown */}
              {showSettingsDropdown && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  right: 0,
                  background: '#23262a',
                  border: '1px solid #353a40',
                  borderRadius: '10px',
                  padding: '8px',
                  minWidth: '220px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  zIndex: 100
                }}>
                  <div style={{ padding: '4px 8px 6px', fontSize: '0.65rem', fontWeight: 700, color: '#8b919a', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Organisation
                  </div>
                  <button
                    onClick={handleUserManagement}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '9px 10px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '7px',
                      color: '#cbd5e1',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s'
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = '#353a40'; e.currentTarget.style.color = '#fff'; }}
                    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    Gestion des utilisateurs
                  </button>
                  <button
                    onClick={() => handleResourcePanel('rooms')}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '9px 10px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '7px',
                      color: '#cbd5e1',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s'
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = '#353a40'; e.currentTarget.style.color = '#fff'; }}
                    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    Gestion des lieux
                  </button>
                  <button
                    onClick={() => handleResourcePanel('vehicles')}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '9px 10px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '7px',
                      color: '#cbd5e1',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s'
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = '#353a40'; e.currentTarget.style.color = '#fff'; }}
                    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 16H9m10 0h1a1 1 0 0 0 1-1v-3a4 4 0 0 0-4-4h-1l-2-3H8L6 8H5a4 4 0 0 0-4 4v3a1 1 0 0 0 1 1h1"/>
                      <circle cx="7.5" cy="16.5" r="1.5"/>
                      <circle cx="16.5" cy="16.5" r="1.5"/>
                    </svg>
                    Gestion des véhicules
                  </button>

                  <button
                    onClick={handleActivityMonitor}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '9px 10px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '7px',
                      color: '#cbd5e1',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s'
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = '#353a40'; e.currentTarget.style.color = '#fff'; }}
                    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                    </svg>
                    Activité utilisateurs
                  </button>

                  {/* Séparateur */}
                  <div style={{ height: '1px', background: '#353a40', margin: '6px 0' }} />

                  <div style={{ padding: '4px 8px 6px', fontSize: '0.65rem', fontWeight: 700, color: '#8b919a', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Interface
                  </div>
                  <button
                    onClick={handleHomepageSettings}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '9px 10px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '7px',
                      color: '#cbd5e1',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s'
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = '#353a40'; e.currentTarget.style.color = '#fff'; }}
                    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7" rx="1"/>
                      <rect x="14" y="3" width="7" height="7" rx="1"/>
                      <rect x="3" y="14" width="7" height="7" rx="1"/>
                      <rect x="14" y="14" width="7" height="7" rx="1"/>
                    </svg>
                    Page d'accueil
                  </button>

                  {/* Séparateur */}
                  <div style={{ height: '1px', background: '#353a40', margin: '6px 0' }} />

                  {/* Section Autres — accordéon */}
                  <button
                    onClick={() => setShowAutresSection(v => !v)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 8px 6px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#8b919a', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                      Autres
                    </span>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#8b919a" strokeWidth="2.5" style={{ transition: 'transform 0.2s', transform: showAutresSection ? 'rotate(180deg)' : 'rotate(0deg)', marginTop: '1px' }}>
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>

                  {showAutresSection && (
                    <button
                      onClick={() => { window.location.href = '/rustdesk/'; setShowSettingsDropdown(false); }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '9px 10px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '7px',
                        color: '#cbd5e1',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.15s'
                      }}
                      onMouseOver={e => { e.currentTarget.style.background = '#353a40'; e.currentTarget.style.color = '#fff'; }}
                      onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2"/>
                        <path d="M8 21h8M12 17v4M17 9l-5 4-5-4"/>
                      </svg>
                      RustDesk Monitor
                      <span style={{ marginLeft: 'auto', width: '6px', height: '6px', borderRadius: '50%', background: rustdeskHealth.up === null ? '#64748b' : rustdeskHealth.up ? '#4ade80' : '#f87171', flexShrink: 0 }} />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          
          <button
            onClick={logout}
            style={{
              background: '#23262a',
              border: '1px solid #353a40',
              color: '#94a3b8',
              borderRadius: '8px',
              padding: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
              e.currentTarget.style.color = '#f87171';
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#23262a';
              e.currentTarget.style.color = '#94a3b8';
              e.currentTarget.style.borderColor = '#353a40';
            }}
            title="Se déconnecter"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16,17 21,12 16,7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{ padding: '40px 24px' }}>
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#f87171',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: '800px',
            margin: '0 auto 24px'
          }}>
            {error}
            <button 
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#f87171',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {showActivityMonitor ? (
          <ActivityMonitor />
        ) : showVolumeMonitor ? (
          <VolumeMonitor />
        ) : showHomepageSettings ? (
          <HomepageSettings
            modules={modules}
            onSaved={(categories) => setHomepageCategories(categories)}
          />
        ) : resourcePanel ? (
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 600, margin: '0 0 16px' }}>
              {resourcePanel === 'rooms' ? 'Gestion des lieux' : 'Gestion des véhicules'}
            </h2>
            <iframe
              title={resourcePanel === 'rooms' ? 'Gestion des lieux' : 'Gestion des véhicules'}
              src={`/hr/?embed=1&view=${resourcePanel}`}
              style={{ width: '100%', height: 'calc(100vh - 160px)', border: '1px solid #353a40', borderRadius: '12px', background: '#121212' }}
            />
          </div>
        ) : !showUserManagement ? (
          <>
            {/* Welcome Section */}
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <h1 style={{
                fontSize: '1.75rem',
                fontWeight: 600,
                color: '#fff',
                margin: '0 0 8px 0'
              }}>
                Tableau de bord
              </h1>
              <p style={{
                fontSize: '0.95rem',
                color: '#94a3b8',
                margin: 0
              }}>
                Sélectionnez un module pour commencer
              </p>
              
              {/* Indicateurs Agent et DaVinci */}
              {(user?.role === 'admin' || getUserModuleAccess().dashboard?.agent) && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: '10px',
                  marginTop: '20px',
                }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 14px',
                    background: '#23262a',
                    border: '1px solid #353a40',
                    borderRadius: '8px',
                    fontSize: '0.85rem'
                  }}>
                    <span style={{ color: '#64748b' }}>Agent</span>
                    <span style={{
                      padding: '2px 10px',
                      borderRadius: 9999,
                      background: agentHealth.up === null ? 'rgba(148, 163, 184, 0.12)' : agentHealth.up ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                      color: agentHealth.up === null ? '#94a3b8' : agentHealth.up ? '#4ade80' : '#f87171',
                      border: `1px solid ${agentHealth.up === null ? 'rgba(148, 163, 184, 0.25)' : agentHealth.up ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}>
                      {agentHealth.up === null ? '···' : agentHealth.up ? 'UP' : 'DOWN'}
                    </span>
                    {agentHealth.latencyMs !== null && (
                      <span style={{ color: '#8b919a', fontSize: '0.8rem' }}>
                        {agentHealth.latencyMs}ms
                      </span>
                    )}
                  </div>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 14px',
                    background: '#23262a',
                    border: '1px solid #353a40',
                    borderRadius: '8px',
                    fontSize: '0.85rem'
                  }}>
                    <span style={{ color: '#64748b' }}>DaVinci</span>
                    <span style={{
                      padding: '2px 10px',
                      borderRadius: 9999,
                      background: davinciStatus.running === null
                        ? 'rgba(148, 163, 184, 0.12)'
                        : davinciStatus.running
                          ? 'rgba(34, 197, 94, 0.15)'
                          : 'rgba(239, 68, 68, 0.15)',
                      color: davinciStatus.running === null
                        ? '#94a3b8'
                        : davinciStatus.running
                          ? '#4ade80'
                          : '#f87171',
                      border: `1px solid ${davinciStatus.running === null
                        ? 'rgba(148, 163, 184, 0.25)'
                        : davinciStatus.running
                          ? 'rgba(34, 197, 94, 0.3)'
                          : 'rgba(239, 68, 68, 0.3)'}`,
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}>
                      {davinciStatus.running === null ? '···' : davinciStatus.running ? 'OUVERT' : 'FERMÉ'}
                    </span>
                    {davinciStatus.running && (
                      <span style={{ color: davinciStatus.projectOpen ? '#94a3b8' : '#fbbf24', fontSize: '0.8rem' }}>
                        {davinciStatus.projectOpen
                          ? (davinciStatus.projectName || 'projet ouvert')
                          : 'sans projet'}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modules Grid */}
            <div className="home-categories">
              {homepageSections.map((section) => (
                <section key={section.id} className="home-category">
                  {section.name && (
                    <h2 className="home-category-title">{section.name}</h2>
                  )}
                  <div className="modules-grid">
                    {section.modules.map((module) => renderModuleTile(module))}
                  </div>
                </section>
              ))}
            </div>

            {accessibleModules.length === 0 && (
              <div style={{
                textAlign: 'center',
                background: '#23232a',
                borderRadius: '18px',
                padding: '48px',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <h3 style={{ color: '#ffa600', marginBottom: '16px' }}>
                  Aucun module accessible
                </h3>
                <p style={{ opacity: 0.8 }}>
                  Contactez un administrateur pour obtenir l'accès aux modules.
                </p>
              </div>
            )}
          </>
        ) : (
          /* User Management Section - Modern Design */
          <div style={{ maxWidth: '1040px', margin: '0 auto' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              gap: '16px',
              marginBottom: '20px',
              flexWrap: 'wrap'
            }}>
              <div>
                <h2 style={{
                  color: '#fff',
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  margin: '0 0 4px 0'
                }}>
                  Gestion des Utilisateurs
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
                  {users.length} compte{users.length > 1 ? 's' : ''} — un profil préremplit les cases, sans toucher aux comptes qui ne l’utilisent pas
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ display: 'flex', background: '#23262a', border: '1px solid #353a40', borderRadius: 10, padding: 4 }}>
                  <button type="button" onClick={() => setUserPane('people')} style={{ border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, background: userPane === 'people' ? '#181b1f' : 'transparent', color: userPane === 'people' ? '#fff' : '#94a3b8' }}>Comptes</button>
                  <button type="button" onClick={() => setUserPane('groups')} style={{ border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, background: userPane === 'groups' ? '#181b1f' : 'transparent', color: userPane === 'groups' ? '#fff' : '#94a3b8' }}>Groupes</button>
                  <button type="button" onClick={() => setUserPane('profiles')} style={{ border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, background: userPane === 'profiles' ? '#181b1f' : 'transparent', color: userPane === 'profiles' ? '#fff' : '#94a3b8' }}>Profils</button>
                </div>
                {userPane === 'people' && (
                  <button
                    onClick={handleCreateUser}
                    style={{
                      background: '#f59e0b',
                      color: '#181b1f',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    + Nouvel utilisateur
                  </button>
                )}
              </div>
            </div>

            {userPane === 'profiles' ? (
              <AccessProfiles modules={modules} users={users} onProfilesChanged={() => { reloadProfiles(); fetchData(); }} />
            ) : userPane === 'groups' ? (
              <UserGroupsPanel users={users} />
            ) : (
            <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
              <input
                value={userQuery}
                onChange={(event) => setUserQuery(event.target.value)}
                placeholder="Rechercher un nom, un email, un profil…"
                style={{ flex: 1, minWidth: 220, background: '#181b1f', border: '1px solid #353a40', borderRadius: 8, color: '#fff', padding: '10px 12px' }}
              />
              <select
                value={userSort}
                onChange={(event) => setUserSort(event.target.value as typeof userSort)}
                style={{ background: '#181b1f', border: '1px solid #353a40', borderRadius: 8, color: '#fff', padding: '10px 12px' }}
              >
                <option value="name">Trier par nom</option>
                <option value="email">Trier par email</option>
                <option value="role">Trier par rôle</option>
                <option value="status">Actifs d’abord</option>
                <option value="login">Dernière connexion</option>
              </select>
            </div>

            {/* Users List - Card Style */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sortedUsers.map((userItem) => (
                <div 
                  key={userItem.id} 
                  style={{
                    background: '#23262a',
                    border: '1px solid #353a40',
                    borderRadius: '12px',
                    padding: '20px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s',
                    opacity: userItem.active ? 1 : 0.6
                  }}
                >
                  {/* User Info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                    <div
                      className={`user-profile-pill${userItem.role === 'admin' ? ' is-admin' : userItem.profileId ? '' : ' is-empty'}`}
                      style={userItem.role === 'admin' ? undefined : profileBadgeStyle(accessProfiles.find((profile) => profile.id === userItem.profileId)?.color)}
                    >
                      {userItem.role === 'admin'
                        ? 'Admin'
                        : (accessProfiles.find((profile) => profile.id === userItem.profileId)?.name || 'Sans profil')}
                    </div>
                    
                    {/* Name & Email */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: '600', 
                        fontSize: '0.95rem',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {userItem.firstName || userItem.lastName 
                          ? `${userItem.firstName || ''} ${userItem.lastName || ''}`.trim()
                          : 'Nom non renseigné'
                        }
                      </div>
                      <div style={{ 
                        color: '#64748b', 
                        fontSize: '0.85rem',
                        marginTop: '2px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {userItem.email}
                      </div>
                    </div>
                  </div>

                  {/* Status & Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {/* Status Badge */}
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '9999px',
                      background: userItem.active 
                        ? 'rgba(34, 197, 94, 0.15)' 
                        : 'rgba(239, 68, 68, 0.15)',
                      color: userItem.active ? '#4ade80' : '#f87171',
                      border: `1px solid ${userItem.active 
                        ? 'rgba(34, 197, 94, 0.3)' 
                        : 'rgba(239, 68, 68, 0.3)'}`,
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {userItem.active ? 'Actif' : 'Inactif'}
                    </span>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEditUser(userItem)}
                        style={{
                          background: '#353a40',
                          border: 'none',
                          color: '#cbd5e1',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        title="Modifier"
                      >
                        Modifier
                      </button>
                      {String(userItem.id) !== String(user?.id) && (
                      <button
                        onClick={() => handleToggleUser(userItem.id, !userItem.active)}
                        style={{
                          background: 'transparent',
                          border: `1px solid ${userItem.active ? 'rgba(239, 68, 68, 0.5)' : 'rgba(34, 197, 94, 0.5)'}`,
                          color: userItem.active ? '#f87171' : '#4ade80',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {userItem.active ? 'Désactiver' : 'Activer'}
                      </button>
                      )}
                      {userItem.id !== user?.id ? (
                        <button
                          onClick={() => handleDeleteUser(userItem.id)}
                          style={{
                            background: 'transparent',
                            border: '1px solid rgba(239, 68, 68, 0.5)',
                            color: '#f87171',
                            borderRadius: '8px',
                            padding: '8px',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Supprimer"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      ) : (
                        <span aria-hidden="true" style={{ width: 34, height: 34, flexShrink: 0 }} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {users.length === 0 && (
              <div style={{
                textAlign: 'center',
                background: '#23262a',
                borderRadius: '12px',
                padding: '48px',
                border: '1px solid #353a40'
              }}>
                <div style={{ color: '#64748b', marginBottom: '16px' }}>
                  Aucun utilisateur enregistré
                </div>
                <button
                  onClick={handleCreateUser}
                  style={{
                    background: '#f59e0b',
                    color: '#181b1f',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Créer le premier utilisateur
                </button>
              </div>
            )}
            </>
            )}
          </div>
        )}
      </div>

      {/* User Form Modal - Modern Design */}
      {showUserForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.72)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="user-modal">
            <div className="user-modal-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h3 style={{
                color: '#fff',
                fontSize: '1.25rem',
                fontWeight: '600',
                margin: 0
              }}>
                {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h3>
              <button
                type="button"
                onClick={() => setShowUserForm(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <form className="user-modal-form" onSubmit={handleFormSubmit}>
              <div className="user-modal-body">
              {formError && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#f87171',
                  padding: '12px 14px',
                  borderRadius: 8,
                  marginBottom: 16,
                  fontSize: '0.875rem'
                }}>
                  {formError}
                </div>
              )}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ color: '#f59e0b', margin: '0 0 16px 0', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Informations personnelles</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#94a3b8', fontSize: '0.875rem' }}>
                      Prénom
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#181b1f',
                        border: '1px solid #353a40',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.95rem'
                      }}
                      placeholder="Entrez le prénom"
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#94a3b8', fontSize: '0.875rem' }}>
                      Nom
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#181b1f',
                        border: '1px solid #353a40',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.95rem'
                      }}
                      placeholder="Entrez le nom"
                    />
                  </div>
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#94a3b8', fontSize: '0.875rem' }}>
                    Email (utilisé comme identifiant) *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#181b1f',
                      border: '1px solid #353a40',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.95rem'
                    }}
                    placeholder="exemple@email.com"
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ color: '#f59e0b', margin: '0 0 16px 0', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Authentification</h4>
                
                {editingUser && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      color: '#94a3b8',
                      fontSize: '0.875rem'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.changePassword || false}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          changePassword: e.target.checked,
                          password: e.target.checked ? prev.password : ''
                        }))}
                        style={{ 
                          width: '16px', 
                          height: '16px',
                          accentColor: '#f59e0b'
                        }}
                      />
                      Changer le mot de passe
                    </label>
                  </div>
                )}

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: (!editingUser || formData.changePassword) ? '1fr 1fr' : '1fr', 
                  gap: '16px' 
                }}>
                  {(!editingUser || formData.changePassword) && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#94a3b8', fontSize: '0.875rem' }}>
                        {editingUser ? 'Nouveau mot de passe *' : 'Mot de passe *'}
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: '#181b1f',
                          border: '1px solid #353a40',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.95rem'
                        }}
                        placeholder={editingUser ? 'Nouveau mot de passe sécurisé' : 'Mot de passe sécurisé'}
                        required={!editingUser || formData.changePassword}
                      />
                    </div>
                  )}
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#94a3b8', fontSize: '0.875rem' }}>
                      Rôle
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => {
                        const role = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          role,
                          ...(role === 'admin' ? { profileId: null } : {}),
                        }));
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#181b1f',
                        border: '1px solid #353a40',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.95rem'
                      }}
                    >
                      <option value="admin">{ROLE_LABELS['admin']}</option>
                      <option value="user">{ROLE_LABELS['user']}</option>
                      <option value="prestataire">{ROLE_LABELS['prestataire']}</option>
                    </select>
                  </div>
                </div>

                {formData.role === 'user' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#94a3b8', fontSize: '0.875rem' }}>
                        Type de contrat
                      </label>
                      <select
                        value={formData.contractType || 'CDI'}
                        onChange={(e) => setFormData(prev => ({ ...prev, contractType: e.target.value }))}
                        style={{ width: '100%', padding: '12px', background: '#181b1f', border: '1px solid #353a40', borderRadius: '8px', color: '#fff', fontSize: '0.95rem' }}
                      >
                        <option value="CDI">CDI</option>
                        <option value="CDD">CDD</option>
                        <option value="APPRENTISSAGE">Apprentissage</option>
                        <option value="STAGE">Stage</option>
                        <option value="INTERIM">Intérim</option>
                        <option value="PRESTATAIRE">Prestataire (hors planning)</option>
                        <option value="CADRE">Cadre (hors planning)</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#94a3b8', fontSize: '0.875rem' }}>
                        Date d'embauche
                      </label>
                      <input
                        type="date"
                        value={formData.contractStartDate || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, contractStartDate: e.target.value }))}
                        style={{ width: '100%', padding: '12px', background: '#181b1f', border: '1px solid #353a40', borderRadius: '8px', color: '#fff', fontSize: '0.95rem', colorScheme: 'dark' }}
                      />
                    </div>
                    {['CDD', 'STAGE', 'INTERIM', 'APPRENTISSAGE'].includes(formData.contractType || '') && (
                      <>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#94a3b8', fontSize: '0.875rem' }}>
                            Date de fin
                          </label>
                          <input
                            type="date"
                            value={formData.contractEndDate || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, contractEndDate: e.target.value }))}
                            style={{ width: '100%', padding: '12px', background: '#181b1f', border: '1px solid #353a40', borderRadius: '8px', color: '#fff', fontSize: '0.95rem', colorScheme: 'dark' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#94a3b8', fontSize: '0.875rem' }}>
                            Heures par semaine
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={48}
                            step={0.5}
                            value={formData.weeklyHours ?? 35}
                            onChange={(e) => setFormData(prev => ({ ...prev, weeklyHours: parseFloat(e.target.value) }))}
                            style={{ width: '100%', padding: '12px', background: '#181b1f', border: '1px solid #353a40', borderRadius: '8px', color: '#fff', fontSize: '0.95rem' }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {formData.role === 'admin' ? (
                <p style={{ color: '#fbbf24', fontSize: '0.875rem', margin: '0 0 8px' }}>
                  Un administrateur a accès à toutes les applications.
                </p>
              ) : (
              <>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#94a3b8', fontSize: '0.875rem' }}>
                  Profil
                </label>
                <select
                  value={formData.profileId ?? ''}
                  onChange={(e) => applyAccessProfile(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#181b1f',
                    border: '1px solid #353a40',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.95rem'
                  }}
                >
                  <option value="">Sans profil — droits actuels</option>
                  {accessProfiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>{profile.name}</option>
                  ))}
                </select>
                <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '8px 0 0' }}>
                  {formData.profileId
                    ? 'Les droits suivent ce profil. Le choix d’émissions reste sur ce compte et conserve le profil : une mise à jour du profil l’applique ici aussi. Modifier une autre case détache le compte.'
                    : 'Choisir un profil remplit les droits. Les autres comptes ne changent pas tant que vous n’enregistrez pas.'}
                </p>
              </div>

              <button
                type="button"
                className="rights-collapse"
                onClick={() => setRightsOpen((open) => !open)}
              >
                Droits des applications
                <span>{rightsOpen ? 'Masquer' : 'Afficher'}</span>
              </button>
              {rightsOpen && (
                <div style={{ marginBottom: '24px' }}>
                    <>
                      {DEFAULT_ROLE_PERMISSIONS[formData.role] && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                          <button type="button" className="mag-action" onClick={applyDefaultPermissions}>Appliquer le défaut du rôle</button>
                        </div>
                      )}
                      <ModuleAccessGrid
                        modules={modules.map((module) => ({ id: module.id, name: module.name }))}
                        access={formData.moduleAccess}
                        onChange={(moduleAccess) => setFormData((prev) => ({ ...prev, profileId: null, moduleAccess }))}
                        renderExtra={(moduleId) => {
                          if (moduleId === 'achats') {
                            const selected: string[] = Array.isArray((formData.moduleAccess as any).achats?.lists)
                              ? (formData.moduleAccess as any).achats.lists.map((item: unknown) => String(item))
                              : [];
                            return (
                              <div style={{ marginTop: 10 }}>
                                <div className="mag-group-label">Listes visibles</div>
                                <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 8px' }}>
                                  Cochez les listes que cet utilisateur peut ouvrir. Une nouvelle liste apparaît ici dès sa création.
                                </p>
                                {achatsListsError && (
                                  <p style={{ color: '#f87171', fontSize: 11, margin: '0 0 8px' }}>{achatsListsError}</p>
                                )}
                                <div className="mag-chips">
                                  {achatsLists.map((list) => (
                                    <button
                                      key={list.id}
                                      type="button"
                                      className={`mag-chip${selected.includes(list.id) ? ' is-on' : ''}`}
                                      onClick={() => toggleAchatsList(list.id, !selected.includes(list.id))}
                                    >
                                      {list.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          if (moduleId === 'vault') {
                            const selected: string[] = Array.isArray((formData.moduleAccess as any).vault?.groups)
                              ? (formData.moduleAccess as any).vault.groups.map((item: unknown) => String(item))
                              : [];
                            return (
                              <div style={{ marginTop: 10 }}>
                                <div className="mag-group-label">Groupes visibles</div>
                                <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 8px' }}>
                                  Cochez les groupes Vault que cet utilisateur peut ouvrir. Un nouveau groupe apparaît ici dès sa création.
                                </p>
                                {vaultGroupsError && (
                                  <p style={{ color: '#f87171', fontSize: 11, margin: '0 0 8px' }}>{vaultGroupsError}</p>
                                )}
                                <div className="mag-chips">
                                  {vaultGroups.map((group) => (
                                    <button
                                      key={group.id}
                                      type="button"
                                      className={`mag-chip${selected.includes(group.id) ? ' is-on' : ''}`}
                                      onClick={() => toggleVaultGroup(group.id, !selected.includes(group.id))}
                                    >
                                      {group.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          if (moduleId === 'captations') {
                            const selected: string[] = Array.isArray((formData.moduleAccess as any).captations?.sites)
                              ? (formData.moduleAccess as any).captations.sites.map((item: unknown) => String(item))
                              : [];
                            return (
                              <div style={{ marginTop: 10 }}>
                                <div className="mag-group-label">Dossiers visibles</div>
                                <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 8px' }}>
                                  Cochez les dossiers que cet utilisateur peut ouvrir. Un nouveau dossier apparaît ici dès sa création.
                                </p>
                                {captationsSitesError && (
                                  <p style={{ color: '#f87171', fontSize: 11, margin: '0 0 8px' }}>{captationsSitesError}</p>
                                )}
                                <div className="mag-chips">
                                  {captationsSites.map((site) => (
                                    <button
                                      key={site.id}
                                      type="button"
                                      className={`mag-chip${selected.includes(site.id) ? ' is-on' : ''}`}
                                      onClick={() => toggleCaptationsSite(site.id, !selected.includes(site.id))}
                                    >
                                      {site.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          if (moduleId !== 'extraits') return null;
                          return (
                          <div style={{ marginTop: 10 }}>
                            <div className="mag-group-label">Émissions</div>
                            <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 8px' }}>
                              Aucune pastille : toutes les émissions, avec Accès complet. En choisir une retire Accès complet et limite cette personne à ces émissions. Le profil reste attaché.
                            </p>
                            <input
                              type="search"
                              value={extraitsShowQuery}
                              onChange={(e) => setExtraitsShowQuery(e.target.value)}
                              placeholder="Filtrer une émission…"
                              style={{
                                width: '100%',
                                marginBottom: 8,
                                background: '#181b1f',
                                border: '1px solid #353a40',
                                borderRadius: 8,
                                color: '#e2e8f0',
                                padding: '8px 10px',
                                fontSize: 12
                              }}
                            />
                            {extraitsShowsError && (
                              <p style={{ color: '#f87171', fontSize: 11, margin: '0 0 8px' }}>{extraitsShowsError}</p>
                            )}
                            <div className="mag-chips" style={{ maxHeight: 160, overflowY: 'auto' }}>
                              {(() => {
                                const selected: string[] = Array.isArray((formData.moduleAccess as any).extraits?.shows)
                                  ? (formData.moduleAccess as any).extraits.shows.map((item: unknown) => String(item).trim().toUpperCase())
                                  : [];
                                const query = extraitsShowQuery.trim().toLowerCase();
                                const known = new Set(extraitsShows.map((show) => show.abbreviation));
                                const orphans = selected.filter((code) => !known.has(code));
                                const visible = extraitsShows.filter((show) =>
                                  !query || show.title.toLowerCase().includes(query) || show.abbreviation.toLowerCase().includes(query)
                                );
                                return (
                                  <>
                                    {orphans.map((code) => (
                                      <button key={code} type="button" className="mag-chip is-on" onClick={() => toggleExtraitsShow(code, false)}>{code}</button>
                                    ))}
                                    {visible.map((show) => (
                                      <button
                                        key={show.abbreviation}
                                        type="button"
                                        className={`mag-chip${selected.includes(show.abbreviation) ? ' is-on' : ''}`}
                                        onClick={() => toggleExtraitsShow(show.abbreviation, !selected.includes(show.abbreviation))}
                                      >
                                        {show.abbreviation}
                                      </button>
                                    ))}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                          );
                        }}
                      />
                    </>
                </div>
              )}
              </>
              )}
              </div>
              <div className="user-modal-foot">
                <button
                  type="button"
                  onClick={() => setShowUserForm(false)}
                  style={{
                    background: '#353a40',
                    border: 'none',
                    color: '#94a3b8',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  style={{
                    background: '#f59e0b',
                    color: '#181b1f',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  {editingUser ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        
        input:focus, select:focus {
          outline: none;
          border-color: #ffa600 !important;
        }
        
        button:hover {
          opacity: 0.9;
        }
        
        tr:hover {
          background: rgba(255, 166, 0, 0.05) !important;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
